/**
 * Статусы спринтов, используются для задач. Модуль для работы с API
 * @module sprintStatuses
 */

import { maxPerPage } from "@/constants";

/**
 * Получение списка статусов. Поддерживает пагинацию, поиск и фильтры
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список статусов
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params) => {
  const queryString = new URLSearchParams(params).toString();

  try {
    const response = await fetch(`/api/sprintstatuses?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение полного списка статусов без пагинации
 * @param {Object} params - Параметры запроса
 * @returns {Promise} - список статусов
 * @throws {Error} - ошибка при получении данных
 */
export const getStatusOptions = async (params) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(`/api/sprintstatuses?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.items;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение статуса по UUID
 * @param {string} uuid - UUID статуса
 * @returns {Promise} - данные статуса
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/sprintstatus?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание статуса
 * @param {Object} item - данные статуса
 * @returns {Promise} - созданный статус
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/sprintstatus", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление статуса
 * @param {Object} item - данные статуса
 * @returns {Promise} - обновленный статус
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/sprintstatus", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление статуса
 * @param {Object} item - данные статуса
 * @returns {Promise} - удаленный статус
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/sprintstatus", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};
